import React, { useRef, useEffect } from 'react'
import { useDataProvider, useNotify } from 'react-admin'
import { Dialog } from '@mui/material'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { useSubmitLoader } from '@src/logic/contexts/utils/SubmitContext.tsx'
import { UserForm, UserFormType, parseUserFormToBackend } from '@src/pages/User/Show/CreateOrEdit/UserForm.tsx'
import styles from '../AccountUsers.module.scss'
import { cn } from '@src/utils/cn'

type CreateUserModalProps = {
  accountId: string
  accountName: string
  onClosed?: (reason: 'created' | 'cancelled') => void
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ accountId, accountName, onClosed }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const { hideModal } = useModal()
  const { isFormSubmitting, setIsFormSubmitting } = useSubmitLoader()

  const modalRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    modalRef.current?.focus()
  }, [])

  const clearAndClose = () => {
    localStorage.removeItem('userCreateForm')
    onClosed?.('cancelled')
    hideModal()
  }

  const onSubmit = async (formData: UserFormType) => {
    if (isFormSubmitting) return
    if (!accountId) {
      notify('Missing account id. Please try again.', { type: 'warning' })
      return
    }

    setIsFormSubmitting(true)

    try {
      await dataProvider.create('User', {
        data: {
          ...parseUserFormToBackend(formData),
          user_account_id: accountId,
        },
      })

      notify('User created successfully', { type: 'success' })
      // localStorage.removeItem('userCreateForm')
      onClosed?.('created')
      hideModal()
    } catch (error: any) {
      const errorMessage = error?.message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  // keep modal consistent with your other dialogs
  const handleBackdropClick = (_: React.MouseEvent<HTMLElement>, reason: string) => {
    if (reason !== 'backdropClick') {
      clearAndClose()
    }
  }

  return (
    <Dialog open onClose={handleBackdropClick} role='dialog' aria-labelledby='create-user-dialog-title' maxWidth='sm' fullWidth>
      <div className={styles.dialogHeader}>
        <h3>Add New Team Member to {accountName}</h3>
      </div>

      <div data-page='create-user-model' className={cn(styles.dialogContent)}>
        <UserForm onSubmit={onSubmit} onCancel={clearAndClose} isSubmitting={isFormSubmitting} />
      </div>
    </Dialog>
  )
}

export default CreateUserModal
